import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import { FeedbackContentType, FeedbackView, SecretaryJudgeValues } from '../feedback.service';
import { categoryResultsUrl, eventPageUrl } from '../feedback-links';
import { CriteriaLabelsService } from '../criteria-labels.service';
import { SportService } from 'app/services/sport/sport.service';
import { pointTypeOfJudge, valueKeys } from 'app/services/sport/judging-rules';

type Criterion = {
  label: string;
  value: number;
};

type FeedbackText = {
  labelKey: string;
  text?: string;
  /**
   * Shown instead of the judge's text when the stored feedback could not be read.
   */
  textKey?: string;
};

type JudgeCard = {
  judgeCode: string;
  criteria: Criterion[];
  total: number;
  texts: FeedbackText[];
  collapsed: boolean;
};

type Program = {
  resultCategory: number | null;
  judges: JudgeCard[];
  collapsed: boolean;
};

type Round = {
  roundName: string;
  programs: Program[];
};

@Component({
  selector: 'tanvo-feedback-view',
  standalone: true,
  imports: [TranslateModule, DecimalPipe],
  templateUrl: './tanvo-feedback-view.component.html',
  styleUrls: ['./tanvo-feedback-view.component.scss'],
})
export class TanvoFeedbackViewComponent implements OnInit {
  @Input() view: FeedbackView;

  rounds: Round[] = [];

  constructor(
    private criteriaLabels: CriteriaLabelsService,
    private sportService: SportService
  ) {}

  ngOnInit() {
    this.applyData(this.view);
  }

  get eventUrl(): string {
    return eventPageUrl(this.view.eventId);
  }

  get resultsUrl(): string {
    return categoryResultsUrl(this.view.eventId, this.view.categoryId);
  }

  /**
   * The result category the participant got, shown instead of the points. The categories judged this way have one
   * performance, so the first category found stands for the whole page.
   */
  get resultCategory(): number | null {
    const categories = this.rounds
      .flatMap((round) => round.programs)
      .map((program) => program.resultCategory)
      .filter((category) => category !== null);
    return categories.length ? categories[0] : null;
  }

  get totalPrograms(): number {
    return this.rounds.reduce((sum, round) => sum + round.programs.length, 0);
  }

  toggleProgram(program: Program) {
    program.collapsed = !program.collapsed;
  }

  toggleJudge(judge: JudgeCard) {
    judge.collapsed = !judge.collapsed;
  }

  anyExpanded(): boolean {
    return this.rounds.some((round) =>
      round.programs.some((program) => !program.collapsed || program.judges.some((judge) => !judge.collapsed))
    );
  }

  toggleAll() {
    const collapse = this.anyExpanded();
    this.rounds.forEach((round) =>
      round.programs.forEach((program) => {
        program.collapsed = collapse;
        program.judges.forEach((judge) => (judge.collapsed = collapse));
      })
    );
  }

  private applyData(view: FeedbackView) {
    this.rounds = view.rounds.map((round) => ({
      roundName: round.roundName,
      programs: round.programs.map((program) => ({
        collapsed: false,
        resultCategory: program.resultCategory ?? null,
        judges: [...program.feedbacks]
          .sort((a, b) => this.compareJudgeCode(a.judgeCode, b.judgeCode))
          .map((feedback) => {
            const values = this.orderedValues(feedback.judgeValues, view.judgingRulesProfileId, feedback.judgeCode);
            const labels = this.criteriaLabels.labels(
              'tanvo',
              view.judgingRulesProfileId,
              feedback.judgeCode,
              values.length
            );
            return {
              judgeCode: feedback.judgeCode,
              criteria: values.map((value, index) => ({ label: labels[index], value })),
              total: values.reduce((sum, value) => sum + value, 0),
              texts: this.feedbackTexts(feedback.feedback, feedback.contentType, program.feedbackFields ?? []),
              collapsed: false,
            };
          }),
      })),
    }));
  }

  /**
   * A judge either writes a single free text or fills in the named text fields of the category, which arrive as a
   * JSON object keyed by the field id.
   */
  private feedbackTexts(
    feedback: string | null,
    contentType: FeedbackContentType | null | undefined,
    fieldIds: string[]
  ): FeedbackText[] {
    if (contentType !== 'JSON') {
      return feedback ? [{ labelKey: 'viewFeedback.judgeFeedback', text: feedback }] : [];
    }
    return this.parseFeedbackFields(feedback, fieldIds);
  }

  private parseFeedbackFields(feedback: string | null, fieldIds: string[]): FeedbackText[] {
    if (!feedback) {
      return [];
    }
    let fields: Record<string, unknown>;
    try {
      const content = JSON.parse(feedback);
      fields = content?.feedback ?? content;
    } catch {
      return [{ labelKey: 'viewFeedback.judgeFeedback', textKey: 'viewFeedback.feedbackUnavailable' }];
    }
    return fieldIds
      .filter((field) => typeof fields?.[field] === 'string' && (fields[field] as string).trim())
      .map((field) => ({ labelKey: `viewFeedback.feedback.${field}`, text: fields[field] as string }));
  }

  private orderedValues(
    judgeValues: SecretaryJudgeValues | null,
    profileId: string | undefined,
    judgeCode: string
  ): number[] {
    if (!judgeValues || !profileId) {
      return [];
    }
    const pointType = pointTypeOfJudge(
      this.sportService.getJudgingRulesProfileAttr(profileId, 'judgeGroups'),
      judgeCode
    );
    return valueKeys(this.sportService.getJudgingRulesProfileAttr(profileId, 'pointTypes'), pointType)
      .filter((key) => judgeValues[key] !== undefined && judgeValues[key] !== null)
      .map((key) => judgeValues[key]);
  }

  private compareJudgeCode(a: string, b: string): number {
    const prefixA = a.replace(/\d+$/, '');
    const prefixB = b.replace(/\d+$/, '');
    if (prefixA !== prefixB) {
      return prefixA.localeCompare(prefixB);
    }
    return this.judgeNumber(a) - this.judgeNumber(b);
  }

  private judgeNumber(code: string): number {
    const match = code.match(/\d+$/);
    return match ? Number(match[0]) : 0;
  }
}
